// Repos data layer. Reads `GET /v1/repos` (cursor-paginated `{ data, next_cursor }`)
// and maps the API projection to the row the grid + <RepoCard/> render. Mirrors
// lib/keys / lib/billing: round-trip over `apiFetch` reusing the dev session bearer.
//
// ONE page per call, and the cursor is honoured. This hook used to drop
// `next_cursor` on the floor, so page one (50 rows) WAS the list — and because
// `/v1/repos` advertises pagination ONLY (`REPO_LIST_SPEC = {}` in
// `apps/api/src/routes/repos.ts`: no `q`, no `filter`, no `sort`), every consumer
// filters client-side over what it holds. A repo on page two answered "no
// matches". Consumers now walk the pages with `usePagedQuery(…, { exhaust: true })`.

import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch, withCursor } from './api';
import { type ApiCall, sendApiCall } from './api-call';
import { type Page, type PagedList, usePagedQuery } from './paged';
import { SESSION_TOKEN } from './session';

/** The `/v1/repos` projection — see `apps/api/src/routes/repos.ts` `projectRepo`. */
type ApiRepo = {
  readonly id: string;
  readonly full_name: string;
  readonly default_branch: string;
  readonly enabled: boolean;
  readonly policy_id: string | null;
  readonly policy_hash: string | null;
  /**
   * The server re-validating the active snapshot against the CURRENT schema
   * (#980): `null` = no snapshot. Optional only for an older API mid-deploy.
   */
  readonly policy_valid?: boolean | null;
  /** Latest agent activity (#980); `null` = no sessions yet. */
  readonly last_activity_iso?: string | null;
  readonly created_at: string;
  readonly coderabbit_warnings: number;
};

/** Row the grid + <RepoCard/> render. Re-exported from `routes/Repos` for callers. */
export type RepoRow = {
  /** Opaque repo id — the `:repoId` a per-repo LLM route pins on (`lib/llmRoutes.ts`). */
  readonly id: string;
  readonly full_name: string;
  readonly owner: string;
  readonly enabled: boolean;
  readonly policy_status: 'valid' | 'invalid' | 'missing' | 'unknown';
  readonly last_activity_iso: string | null;
  /**
   * Latest `.coderabbit.yaml` translate-warning count (deferred/unknown/invalid
   * keys) — advisory only, never blocks a review. Rendered by
   * `<PolicyWarningsBadge/>` next to the policy badge.
   */
  readonly coderabbit_warnings: number;
};

/** Exported for the pure-mapping unit test (`repos.test.ts`). */
export function toRow(r: ApiRepo): RepoRow {
  return {
    id: r.id,
    full_name: r.full_name,
    owner: r.full_name.split('/')[0] ?? r.full_name,
    enabled: r.enabled,
    policy_status: policyStatus(r),
    last_activity_iso: r.last_activity_iso ?? null,
    coderabbit_warnings: r.coderabbit_warnings,
  };
}

/**
 * `policy_valid` (#980) drives the badge + the toolbar's valid/invalid filter,
 * on the grid AND the repo detail header — the server's one resolve, projected
 * once. Without the field (older API mid-deploy) the snapshot pointer alone can
 * only say "has one" — 'unknown' — never real validity.
 */
export function policyStatus(
  r: Pick<ApiRepo, 'policy_id' | 'policy_valid'>,
): RepoRow['policy_status'] {
  if (r.policy_valid === true) return 'valid';
  if (r.policy_valid === false) return 'invalid';
  if (r.policy_valid === null) return 'missing';
  return r.policy_id ? 'unknown' : 'missing';
}

export const REPOS_QUERY_KEY = 'repos';

/**
 * ONE page at `cursor`. Idle (no fetch) when there's no session. Hand it to
 * `usePagedQuery`: the cursor accessor is part of the query key, so a load-more
 * (or an `exhaust` walk) refetches by itself.
 *
 * `includeAll` asks `?include=all` (#2540) — every row the account holds, not
 * just the enabled ones. It is part of the query KEY as well as the URL, or the
 * two reads would share a cache entry and the narrower one would serve the
 * wider one's rows.
 */
export function useRepos(
  cursor: () => string | null,
  token: string | null = SESSION_TOKEN,
  includeAll = false,
) {
  return createQuery<Page<RepoRow>>(() => ({
    queryKey: [REPOS_QUERY_KEY, cursor(), token, activeAccountId(), includeAll],
    enabled: token !== null,
    queryFn: async () => {
      const page = await apiFetch<Page<ApiRepo>>(
        withCursor(includeAll ? '/v1/repos?include=all' : '/v1/repos', cursor()),
        token,
      );
      // `?? null` normalises an envelope that omits the field: `undefined` there
      // would arm "Load more" over a page that does not exist.
      return { data: page.data.map(toRow), next_cursor: page.next_cursor ?? null };
    },
  }));
}

/**
 * The account's ENABLED repos, walked WHOLE. The overview surfaces and the
 * pickers read it, so the two paging decisions live here instead of being
 * restated per route: `full_name` is the row identity (the projection carries
 * no `id`), and `exhaust` is what makes the Repos toolbar's client-side filter
 * honest.
 */
export function useRepoList(token: string | null = SESSION_TOKEN): PagedList<RepoRow> {
  return usePagedQuery<RepoRow>((cursor) => useRepos(cursor, token), {
    key: 'full_name',
    exhaust: true,
  });
}

/**
 * The Repos GRID's list (#2540): EVERY row, enabled or not. The grid is the one
 * surface that must show a repo you can turn ON, and two populations are only
 * reachable here — a repo that predates the App install (discovered dormant by
 * the installation reconcile, announced by no webhook that exists) and a repo
 * the maintainer switched OFF, which the enabled-only read drops entirely, so
 * until now nothing in the dashboard could offer it back.
 *
 * Deliberately NOT the default: `useRepoList` still serves enabled-only, so the
 * overview counts, the pickers and the command palette keep meaning "repos we
 * operate". Widening those would quietly offer a disabled repo as a work target.
 */
export function useRepoGrid(token: string | null = SESSION_TOKEN): PagedList<RepoRow> {
  return usePagedQuery<RepoRow>((cursor) => useRepos(cursor, token, true), {
    key: 'full_name',
    exhaust: true,
  });
}

/**
 * Flip a repo's enablement (`PATCH /v1/repos/:owner/:repo` `{enabled}`, #972).
 * 200 answers the flipped projection — but the DEFAULT `/v1/repos` lists
 * ENABLED repos only, so after a disable the row is GONE from that read:
 * callers `reload()` and let it vanish rather than patching a row the server no
 * longer serves. On the grid (`useRepoGrid`, `?include=all`) it does not
 * vanish — it reloads as a row you can switch back on.
 */
export async function setRepoEnabled(
  repo: RepoEnableTarget,
  enabled: boolean,
  token: string | null = SESSION_TOKEN,
): Promise<RepoRow> {
  return toRow(await sendApiCall<ApiRepo>(repoEnableCall(repo, enabled), token));
}

/**
 * What the flip needs to address the repo, on EITHER surface: `owner/repo`, and
 * nothing else. REST routes on it, and so does the MCP verb — `project add`
 * took a `repo_id` uuid until C5, and the catalog's one repo address has been
 * `owner/name` since (`projectRepoField`, `packages/mcp/src/army/project.ts`).
 * A row's opaque id is not asked for here, so no caller has to carry one to
 * offer the flip.
 */
export type RepoEnableTarget = { readonly full_name: string };

/**
 * The flip as one value — fired by `setRepoEnabled`, shown by `<CopyAsCall/>`.
 *
 * BOTH DIRECTIONS HAVE AN MCP DOOR since arc 101 slice 04 (#4607): `project add`
 * sets `enabled`, `project disable` clears it — the `repositories` row, its
 * history, its policy snapshot and its sessions all stay, and `add` turns it
 * back on. They are two actions rather than one `set_enabled {bool}` because
 * only the ON direction runs the tier gate and the duplicate-name refusal
 * (`packages/mcp/src/army/project.ts`), which is why this picks the verb by
 * direction instead of sending a boolean.
 *
 * Both take the repo by `owner/name`, the argument name and shape their schema
 * declares. The id-keyed call this used to print for `add` was refused by the
 * server the button beside it had just succeeded against.
 */
export function repoEnableCall(repo: RepoEnableTarget, enabled: boolean): ApiCall {
  return {
    capability: 'project.update',
    method: 'PATCH',
    path: repoPath(repo.full_name),
    body: { enabled },
    mcp: {
      kind: 'verb',
      verb: enabled ? 'project.add' : 'project.disable',
      arguments: { repo: repo.full_name },
    },
  };
}

/**
 * Flip a repo's "accept donations" opt-in (`PATCH /v1/repos/:owner/:repo`
 * `{donations_enabled}`, arc 101 slice 02). Sends that field ALONE: the two
 * switches are independent, and echoing back the `enabled` this client last read
 * would let a donations flip silently offboard a repo somebody else just enabled.
 *
 * Answers nothing on purpose. The 200 does carry the flipped projection, but
 * donatable is `enabled AND NOT private AND donations_enabled` — a conjunction
 * evaluated in ONE place server-side — so callers re-READ the repo rather than
 * patching a pill from an echo that only settles one third of it.
 */
export async function setRepoDonationsEnabled(
  fullName: string,
  donationsEnabled: boolean,
  token: string | null = SESSION_TOKEN,
): Promise<void> {
  await apiFetch(repoPath(fullName), token, {
    method: 'PATCH',
    body: JSON.stringify({ donations_enabled: donationsEnabled }),
  });
}

/**
 * `owner/repo` → the route path. Same per-segment encoding as `repoApiBase`
 * (lib/repo-detail), which starts from the router's already-encoded param: the
 * API routes `/:owner/:repo` as two percent-encoded params, so a repo whose name
 * needs escaping addresses the same row from either surface.
 */
function repoPath(fullName: string): string {
  const [owner = '', ...rest] = fullName.split('/');
  return `/v1/repos/${encodeURIComponent(owner)}/${encodeURIComponent(rest.join('/'))}`;
}

/**
 * The loaded rows once page one has landed, `undefined` while it's still in
 * flight OR after it failed outright. Paged rows start as `[]`, so a stat cell
 * reading `rows()` straight would claim "0 repos" for an account whose list
 * never arrived — where both overview bands promise a "—" placeholder until
 * their source resolves. `view.isError` is already scoped to "nothing on
 * screen" (`paged-view.ts`), so a failed load-more keeps the rows it has.
 */
export function loadedRepos(list: PagedList<RepoRow>): readonly RepoRow[] | undefined {
  return list.view.isLoading || list.view.isError ? undefined : list.rows();
}
